import { supabase } from '../client';
import { requireContext, type ActiveContext } from '../activeContext';
import { sendEmail, type SendEmailResult } from './email';
import { sendSms, toE164, type SendSmsResult } from './sms';
import { getSiteConfig } from './siteConfig';
import type { Database, Json } from '../generated';
import type { IntegrationKeySourceLabel } from './integrationKeys';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';

// ---- types ---------------------------------------------------------------

/**
 * Inbound-integration provenance columns added by
 * `supabase/migrations/20260606000200_clients_orders_payments_source_columns.sql`.
 *
 * Present on `orders.orders` and `orders.payments`. The generated
 * Database types lag the migration (codegen runs at deploy), so we
 * intersect them onto the Row types here. `select('*')` returns the
 * underlying columns; this just teaches TypeScript about them so
 * detail pages can render the "imported via <source>" chip without an
 * `as any` cast.
 */
export interface SourceColumns {
  source_integration_key_id: string | null;
  source_external_id: string | null;
  source_label: IntegrationKeySourceLabel | null;
}

/**
 * Wholesaler portal classification + visibility columns added by
 * `supabase/migrations/20260627000100_orders_wholesaler_portal_extensions.sql`.
 *
 * Same generated-type-lag situation as `SourceColumns`: codegen runs
 * at deploy, so we intersect these onto the Row type here so operator
 * UIs can render the "Portal" pill and the visibility toggles without
 * `as any` casts.
 *
 * `order_source` is one of: 'operator' | 'wholesaler_portal' | 'integration'.
 */
export interface WholesalerPortalOrderColumns {
  order_source: 'operator' | 'wholesaler_portal' | 'integration';
  portal_visible_to_wholesaler: boolean;
  wholesaler_locked: boolean;
  wholesaler_submitted_at: string | null;
  wholesaler_submitted_by_portal_user_id: string | null;
}

/**
 * Fulfillment attention columns added by
 * `supabase/migrations/20260627001200_fulfillment_shippo_reconcile_and_attention_alerts.sql`.
 *
 * Set when shipment tracking reaches exception / return-to-sender states so
 * staff can triage affected orders from the main order surfaces.
 */
export interface FulfillmentAttentionOrderColumns {
  fulfillment_needs_attention: boolean;
  fulfillment_attention_reason: string | null;
  fulfillment_attention_last_event: string | null;
  fulfillment_attention_updated_at: string | null;
}

/** Snapshotted at order creation; when set, DB auto-calculates tax_cents. */
interface SalesTaxOrderColumns {
  sales_tax_rate_pct: number | null;
}

export type Order = Database['orders']['Tables']['orders']['Row'] &
  SourceColumns &
  WholesalerPortalOrderColumns &
  FulfillmentAttentionOrderColumns &
  SalesTaxOrderColumns;

/** Order-level sales tax from subtotal after discount (before shipping). */
export function computeSalesTaxCents(
  subtotalCents: number,
  discountCents: number,
  ratePct: number,
): number {
  if (!ratePct || ratePct <= 0) return 0;
  const taxable = Math.max(0, subtotalCents - discountCents);
  return Math.round(taxable * ratePct / 100);
}
// Insert / Update intentionally do NOT carry the SourceColumns intersection:
// operator-driven writes never set the source_* fields (only the
// integration-api edge function does), and the supabase-js builders use
// the generated `RejectExcessProperties` mapped type to forbid unknown
// columns at the call site.
export type OrderInsert = Database['orders']['Tables']['orders']['Insert'];
export type OrderUpdate = Database['orders']['Tables']['orders']['Update'];
export type OrderLine = Database['orders']['Tables']['order_lines']['Row'];
export type OrderLineInsert = Database['orders']['Tables']['order_lines']['Insert'];
export type OrderEvent = Database['orders']['Tables']['order_events']['Row'];
export interface WholesalerPortalPaymentColumns {
  portal_visible_to_wholesaler: boolean;
}

export type Payment = Database['orders']['Tables']['payments']['Row'] &
  SourceColumns &
  WholesalerPortalPaymentColumns;
export type PaymentInsert = Database['orders']['Tables']['payments']['Insert'];
export type OrderStatus = Database['orders']['Enums']['order_status'];
export type OrderType = Database['orders']['Enums']['order_type'];
export type PaymentMethod = Database['orders']['Enums']['payment_method'];

export type OrderSource = 'operator' | 'wholesaler_portal' | 'integration';

export interface ListOrdersInput extends KeysetInput {
  status?: OrderStatus | OrderStatus[];
  search?: string;
  clientId?: string;
  wholesalerId?: string;
  orderSource?: OrderSource | OrderSource[];
}

/** PostgREST `.or()` clause for order list search (number, notes, client snapshot). */
function orderListSearchOrClause(raw: string): string | null {
  const term = raw.trim();
  if (!term) return null;
  const s = `%${term}%`;
  return [
    `number.ilike.${s}`,
    `internal_notes.ilike.${s}`,
    `client_snapshot->>first_name.ilike.${s}`,
    `client_snapshot->>last_name.ilike.${s}`,
    `client_snapshot->>company_name.ilike.${s}`,
    `client_snapshot->>display_name.ilike.${s}`,
    `client_snapshot->>email.ilike.${s}`,
  ].join(',');
}

/**
 * Paginated keyset list of orders in the active store. Mirrors
 * `listClients` -- see that function for the pagination contract details.
 *
 * The composite indexes hit on the hot path:
 *   * `orders_orders_list_keyset_idx` for the unfiltered list.
 *   * `orders_orders_status_keyset_idx` for the (very common) status
 *     filter (drafts / proposal_sent / paid / etc.).
 */
export async function listOrders(
  ctx: ActiveContext,
  input: ListOrdersInput = {},
): Promise<ListResult<Order>> {
  const { orgId, storeId } = requireContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  let q = supabase()
    .schema('orders')
    .from('orders')
    .select('*', selectCountOption(input.countMode))
    .eq('org_id', orgId)
    .eq('store_id', storeId);

  if (input.clientId) q = q.eq('client_id', input.clientId);
  if (input.wholesalerId) q = q.eq('wholesaler_id', input.wholesalerId);
  if (Array.isArray(input.status) && input.status.length > 0) q = q.in('status', input.status);
  else if (typeof input.status === 'string') q = q.eq('status', input.status);
  if (Array.isArray(input.orderSource) && input.orderSource.length > 0) {
    // `order_source` ships in `WholesalerPortalOrderColumns` but is not in
    // the checked-in `generated.ts` yet (typegen lags the prod schema).
    // Cast to `any` so the column-name overload doesn't reject it.
    q = (q as any).in('order_source', input.orderSource);
  } else if (typeof input.orderSource === 'string') {
    q = (q as any).eq('order_source', input.orderSource);
  }
  const searchOr = input.search ? orderListSearchOrClause(input.search) : null;
  if (searchOr) q = q.or(searchOr);

  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as Order[];
  return {
    rows,
    nextCursor: buildNextCursor(rows as unknown as Array<Record<string, unknown>>, pageSize, 'updated_at'),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

/**
 * One-shot small fetch for dashboard widgets that want the most-recent N
 * orders. Returns the bare `Order[]`. Dashboard widgets and pickers
 * should use this; the main list page uses `listOrders` + infinite query.
 */
export async function listOrdersTop(
  ctx: ActiveContext,
  input: Omit<ListOrdersInput, 'cursor' | 'countMode'> & { limit?: number } = {},
): Promise<Order[]> {
  const { rows } = await listOrders(ctx, {
    ...input,
    pageSize: input.limit ?? 6,
    countMode: 'none',
  });
  return rows;
}

export async function getOrder(ctx: ActiveContext, id: string): Promise<Order | null> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('orders')
    .from('orders')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  // Generated Row type lags the source_* columns added by the
  // 20260606000200 migration -- the runtime row includes them, so the
  // cast onto our extended `Order` type is sound.
  return (data ?? null) as Order | null;
}

export async function getOrderLines(ctx: ActiveContext, orderId: string): Promise<OrderLine[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('orders')
    .from('order_lines')
    .select('*')
    .eq('order_id', orderId)
    .order('position', { ascending: true })
    .limit(1000);
  if (error) throw error;
  return data ?? [];
}

export async function getOrderEvents(ctx: ActiveContext, orderId: string): Promise<OrderEvent[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('orders')
    .from('order_events')
    .select('*')
    .eq('order_id', orderId)
    .order('at', { ascending: false })
    .limit(500);
  if (error) throw error;
  return data ?? [];
}

// ---- create / update -----------------------------------------------------

export interface CreateOrderInput {
  clientId: string;
  proposalMessage?: string | null;
  currency?: string;
  discountCents?: number;
  taxCents?: number;
  shippingCents?: number;
  internalNotes?: string | null;
  lines?: Array<Omit<OrderLineInsert, 'org_id' | 'store_id' | 'order_id' | 'id'>>;
  // Fulfillment
  orderType?: OrderType;
  shippingAddressSnapshot?: Json | null;
  billingAddressSnapshot?: Json | null;
  pickupReadyAt?: string | null;
  // Wholesaler drop-ship link (optional). Both must be set together or
  // neither -- the caller decides; the DB doesn't enforce coupling yet.
  wholesalerId?: string | null;
  wholesalerRecipientId?: string | null;
}

async function nextNumber(storeId: string, prefix: string): Promise<string> {
  // We call the per-store entry point (added 20260525021300). The older
  // `fn_next_number(p_org_id, p_prefix)` signature was kept as a legacy
  // shim for browser bundles still in the wild, but new code goes
  // through the explicit per-store function so multi-store orgs get
  // distinct sequences.
  const { data, error } = await supabase()
    .schema('orders')
    .rpc('fn_next_number_for_store', { p_store_id: storeId, p_prefix: prefix });
  if (error) throw error;
  return data as unknown as string;
}

/**
 * Resolve the per-store proposal / invoice prefix. Falls back to the
 * historical 'PRO' / 'INV' if `core.store_settings` has no row yet (e.g.
 * a brand-new store created before the seed migration ran -- shouldn't
 * happen, but we don't want a missing row to block creating an order).
 */
async function getStorePrefix(
  storeId: string,
  kind: 'order' | 'invoice',
): Promise<string> {
  const { data } = await supabase()
    .schema('core')
    .from('store_settings')
    .select('order_prefix, invoice_prefix')
    .eq('store_id', storeId)
    .maybeSingle();
  if (kind === 'order') return data?.order_prefix ?? 'PRO';
  return data?.invoice_prefix ?? 'INV';
}

async function getStoreSalesTaxRatePct(storeId: string): Promise<number> {
  const { data, error } = await supabase()
    .schema('core')
    .from('store_settings')
    .select('sales_tax_rate_pct')
    .eq('store_id', storeId)
    .maybeSingle();
  if (error) throw error;
  const row = data as { sales_tax_rate_pct?: number | string } | null;
  return Number(row?.sales_tax_rate_pct) || 0;
}

export async function createOrder(ctx: ActiveContext, input: CreateOrderInput): Promise<Order> {
  const { orgId, storeId } = requireContext(ctx);
  const prefix = await getStorePrefix(storeId, 'order');
  const number = await nextNumber(storeId, prefix);
  const salesTaxRatePct = await getStoreSalesTaxRatePct(storeId);

  // Pull client snapshot so historical orders still read correctly even if
  // the client is later renamed or archived.
  const { data: client } = await supabase()
    .schema('clients')
    .from('clients')
    .select('id, display_name, first_name, last_name, company_name, email, phone, client_type')
    .eq('id', input.clientId)
    .maybeSingle();

  // sales_tax_rate_pct ships ahead of generated.ts — cast until db:types catches up.
  const orderRow = {
      org_id: orgId,
      store_id: storeId,
      client_id: input.clientId,
      number,
      status: 'draft',
      currency: input.currency ?? 'USD',
      discount_cents: input.discountCents ?? 0,
      tax_cents: input.taxCents ?? 0,
      sales_tax_rate_pct: salesTaxRatePct,
      shipping_cents: input.shippingCents ?? 0,
      proposal_message: input.proposalMessage ?? undefined,
      internal_notes: input.internalNotes ?? undefined,
      client_snapshot: (client ?? {}) as Json,
      order_type: input.orderType ?? 'ship',
      shipping_address_snapshot: input.shippingAddressSnapshot ?? undefined,
      billing_address_snapshot: input.billingAddressSnapshot ?? undefined,
      pickup_ready_at: input.pickupReadyAt ?? undefined,
      wholesaler_id: input.wholesalerId ?? undefined,
      wholesaler_recipient_id: input.wholesalerRecipientId ?? undefined,
    };

  const { data: order, error: orderErr } = await supabase()
    .schema('orders')
    .from('orders')
    .insert(orderRow as OrderInsert)
    .select('*')
    .single();
  if (orderErr) throw orderErr;

  if (input.lines && input.lines.length > 0) {
    const lineRows: OrderLineInsert[] = input.lines.map((l, i) => ({
      org_id: orgId,
      store_id: storeId,
      order_id: (order as Order).id,
      position: l.position ?? i,
      description: l.description,
      quantity: l.quantity ?? 1,
      unit_price_cents: l.unit_price_cents ?? 0,
      discount_cents: l.discount_cents ?? 0,
      tax_rate_pct: l.tax_rate_pct ?? 0,
      metadata: l.metadata ?? {},
    }));
    const { error: linesErr } = await supabase()
      .schema('orders')
      .from('order_lines')
      .insert(lineRows);
    if (linesErr) throw linesErr;
  }

  await recordEvent(ctx, (order as Order).id, 'created', 'Order created.');
  // Reload with computed totals.
  return (await getOrder(ctx, (order as Order).id)) as Order;
}

export interface UpdateOrderInput {
  id: string;
  proposalMessage?: string | null;
  discountCents?: number;
  taxCents?: number;
  shippingCents?: number;
  internalNotes?: string | null;
  dueAt?: string | null;
  orderType?: OrderType;
  shippingAddressSnapshot?: Json | null;
  pickupReadyAt?: string | null;
}

export async function updateOrder(ctx: ActiveContext, input: UpdateOrderInput): Promise<Order> {
  requireContext(ctx);
  const patch: OrderUpdate = {};
  if (input.proposalMessage !== undefined) patch.proposal_message = input.proposalMessage;
  if (input.discountCents !== undefined) patch.discount_cents = input.discountCents;
  if (input.taxCents !== undefined) patch.tax_cents = input.taxCents;
  if (input.shippingCents !== undefined) patch.shipping_cents = input.shippingCents;
  if (input.internalNotes !== undefined) patch.internal_notes = input.internalNotes;
  if (input.dueAt !== undefined) patch.due_at = input.dueAt;
  if (input.orderType !== undefined) patch.order_type = input.orderType;
  if (input.shippingAddressSnapshot !== undefined)
    patch.shipping_address_snapshot = input.shippingAddressSnapshot;
  if (input.pickupReadyAt !== undefined) patch.pickup_ready_at = input.pickupReadyAt;

  const { data, error } = await supabase()
    .schema('orders')
    .from('orders')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  // Force re-trigger of totals recompute if discount/tax/shipping changed.
  if (
    patch.discount_cents !== undefined ||
    patch.tax_cents !== undefined ||
    patch.shipping_cents !== undefined
  ) {
    await supabase()
      .schema('orders')
      .from('order_lines')
      .update({ position: 0 }) // no-op-ish, triggers recompute
      .eq('order_id', input.id)
      .limit(1);
  }
  return data as Order;
}

// ---- lines ---------------------------------------------------------------

export interface UpsertOrderLineInput {
  id?: string;
  orderId: string;
  position?: number;
  description: string;
  quantity?: number;
  unitPriceCents?: number;
  discountCents?: number;
  taxRatePct?: number;
  metadata?: Json;
}

export async function upsertOrderLine(
  ctx: ActiveContext,
  input: UpsertOrderLineInput,
): Promise<OrderLine> {
  const { orgId, storeId } = requireContext(ctx);
  if (input.id) {
    const patch: Partial<OrderLineInsert> = {
      description: input.description,
      quantity: input.quantity ?? 1,
      unit_price_cents: input.unitPriceCents ?? 0,
      discount_cents: input.discountCents ?? 0,
      tax_rate_pct: input.taxRatePct ?? 0,
      metadata: input.metadata ?? {},
    };
    if (input.position !== undefined) patch.position = input.position;
    const { data, error } = await supabase()
      .schema('orders')
      .from('order_lines')
      .update(patch)
      .eq('id', input.id)
      .select('*')
      .single();
    if (error) throw error;
    return data as OrderLine;
  }
  const insert: OrderLineInsert = {
    org_id: orgId,
    store_id: storeId,
    order_id: input.orderId,
    position: input.position ?? 0,
    description: input.description,
    quantity: input.quantity ?? 1,
    unit_price_cents: input.unitPriceCents ?? 0,
    discount_cents: input.discountCents ?? 0,
    tax_rate_pct: input.taxRatePct ?? 0,
    metadata: input.metadata ?? {},
  };
  const { data, error } = await supabase()
    .schema('orders')
    .from('order_lines')
    .insert(insert)
    .select('*')
    .single();
  if (error) throw error;
  return data as OrderLine;
}

export async function deleteOrderLine(ctx: ActiveContext, id: string): Promise<void> {
  requireContext(ctx);
  const { error } = await supabase().schema('orders').from('order_lines').delete().eq('id', id);
  if (error) throw error;
}

// ---- state transitions ---------------------------------------------------

function randomToken(): string {
  // 32 bytes of randomness -> base36 string, 50ish chars, unguessable.
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  let s = '';
  for (const byte of arr) s += byte.toString(36).padStart(2, '0');
  return s;
}

async function recordEvent(
  ctx: ActiveContext,
  orderId: string,
  eventType: string,
  summary: string,
  details: Json = {},
) {
  const { orgId, storeId } = requireContext(ctx);
  const { data: userData } = await supabase().auth.getUser();
  const { error } = await supabase()
    .schema('orders')
    .from('order_events')
    .insert({
      org_id: orgId,
      store_id: storeId,
      order_id: orderId,
      event_type: eventType,
      actor_type: 'user',
      actor_id: userData.user?.id ?? undefined,
      summary,
      details,
    });
  if (error) throw error;
}

export interface SendProposalInput {
  id: string;
  expiresAt?: string | null;
  message?: string | null;
  /**
   * @deprecated Ignored. The public-approval URL is now built inside
   * the `proposal_sent` email template from `{{app_url}}`, which
   * `core.rpc_emit_email` auto-injects from
   * `core.email_settings.default_app_url`. Passing a browser origin
   * here used to leak dev URLs (http://localhost:5173) into client
   * inboxes; the field is kept on the signature so existing callers
   * compile but it has no effect.
   */
  approvalBaseUrl?: string;
  /** Override the email recipient (defaults to the client_snapshot.email). */
  toEmail?: string;
  /**
   * Override the SMS recipient (defaults to the client_snapshot.phone).
   * Accepts any format `toE164` can normalize; the service rejects with
   * an `sms.status='skipped'` outcome if it cannot reach a valid E.164.
   */
  toPhone?: string;
  /**
   * Set to false to suppress the transactional email. Defaults to true
   * for backwards compatibility -- existing callers that just pass
   * `{ id }` continue to send the email.
   */
  sendEmail?: boolean;
  /**
   * Set to true to also (or instead) send the proposal via SMS through
   * the workspace's Sendblue connection. Defaults to false so existing
   * callers do not get an unexpected text charge.
   */
  sendSms?: boolean;
}

export interface SendProposalResult {
  order: Order;
  email: ProposalEmailOutcome;
  sms: ProposalSmsOutcome;
}

export type ProposalEmailOutcome =
  | { status: 'sent';     to: string;  messageId?: string }
  | { status: 'skipped';  reason: string }
  | { status: 'failed';   to: string;  error: string };

export type ProposalSmsOutcome =
  | { status: 'sent';     to: string;  messageId?: string }
  | { status: 'skipped';  reason: string }
  | { status: 'failed';   to: string;  error: string };

export async function sendProposal(
  ctx: ActiveContext,
  input: SendProposalInput,
): Promise<SendProposalResult> {
  const { orgId } = requireContext(ctx);
  const token = randomToken();
  const patch: OrderUpdate = {
    status: 'proposal_sent',
    proposal_sent_at: new Date().toISOString(),
    approval_token: token,
    proposal_expires_at: input.expiresAt ?? undefined,
  };
  if (input.message !== undefined) patch.proposal_message = input.message;
  const { data, error } = await supabase()
    .schema('orders')
    .from('orders')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  await recordEvent(ctx, input.id, 'proposal_sent', 'Proposal sent to client.', { token });

  const order = data as Order;
  const email =
    input.sendEmail === false
      ? ({ status: 'skipped', reason: 'sendEmail=false' } as ProposalEmailOutcome)
      : await sendProposalEmailInternal(ctx, orgId, order, token, {
          to: input.toEmail,
          approvalBaseUrl: input.approvalBaseUrl,
        });

  const sms = input.sendSms
    ? await sendProposalSmsInternal(ctx, orgId, order, token, { to: input.toPhone })
    : ({ status: 'skipped', reason: 'sendSms=false' } as ProposalSmsOutcome);

  return { order, email, sms };
}

export interface ResendProposalEmailInput {
  orderId: string;
  /** Override the recipient (defaults to the client_snapshot.email). */
  toEmail?: string;
  /**
   * @deprecated Ignored. See `SendProposalInput.approvalBaseUrl` -- the
   * URL is built from `core.email_settings.default_app_url` inside the
   * `proposal_sent` template.
   */
  approvalBaseUrl?: string;
}

/**
 * Resends the proposal email for an order already in `proposal_sent` state.
 * Reuses the existing approval_token so the same link the client may have
 * already received still works.
 */
export async function resendProposalEmail(
  ctx: ActiveContext,
  input: ResendProposalEmailInput,
): Promise<ProposalEmailOutcome> {
  const { orgId } = requireContext(ctx);
  const order = await getOrder(ctx, input.orderId);
  if (!order) throw new Error('Order not found.');
  if (!order.approval_token) {
    throw new Error('This order has no approval token yet -- send the proposal first.');
  }
  const outcome = await sendProposalEmailInternal(ctx, orgId, order, order.approval_token, {
    to: input.toEmail,
    approvalBaseUrl: input.approvalBaseUrl,
    eventTypePrefix: 'proposal_resent',
  });
  return outcome;
}

export interface ResendProposalSmsInput {
  orderId: string;
  /** Override the recipient (defaults to the client_snapshot.phone). */
  toPhone?: string;
}

/**
 * Resends the proposal SMS for an order already in `proposal_sent` state
 * via the workspace's Sendblue connection. Reuses the existing
 * approval_token so the link the client may have already tapped still
 * works.
 *
 * Symmetric with `resendProposalEmail` -- both reuse the per-channel
 * "internal" helper used by the initial `sendProposal` send, with an
 * `eventTypePrefix` of `proposal_resent` so the activity feed shows
 * distinct "resent" lines.
 */
export async function resendProposalSms(
  ctx: ActiveContext,
  input: ResendProposalSmsInput,
): Promise<ProposalSmsOutcome> {
  const { orgId } = requireContext(ctx);
  const order = await getOrder(ctx, input.orderId);
  if (!order) throw new Error('Order not found.');
  if (!order.approval_token) {
    throw new Error('This order has no approval token yet -- send the proposal first.');
  }
  const outcome = await sendProposalSmsInternal(ctx, orgId, order, order.approval_token, {
    to: input.toPhone,
    eventTypePrefix: 'proposal_resent_sms',
  });
  return outcome;
}

/**
 * Shared rendering + send + event-logging for proposal emails. Always returns
 * an outcome; never throws. Records an order event describing what happened so
 * the operator can see it in the activity feed.
 */
async function sendProposalEmailInternal(
  ctx: ActiveContext,
  orgId: string,
  order: Order,
  token: string,
  opts: {
    to?: string;
    approvalBaseUrl?: string;
    eventTypePrefix?: string;
  },
): Promise<ProposalEmailOutcome> {
  const snapshot = (order.client_snapshot ?? {}) as Record<string, unknown>;
  const recipient =
    (opts.to && opts.to.trim().length > 0 ? opts.to.trim() : null) ??
    (typeof snapshot.email === 'string' && snapshot.email.trim().length > 0
      ? (snapshot.email as string).trim()
      : null);

  const eventPrefix = opts.eventTypePrefix ?? 'proposal_email';

  if (!recipient) {
    const reason = 'No email address on file for this client.';
    await safeRecord(ctx, order.id, `${eventPrefix}_skipped`, reason, { reason });
    return { status: 'skipped', reason };
  }

  // NB: we do NOT compute the approval URL here any more. The
  // `proposal_sent` email template builds it as
  // `{{app_url}}/p/{{approval_token}}`, and `{{app_url}}` is auto-
  // injected by core.rpc_emit_email from
  // core.email_settings.default_app_url. That way the operator's
  // local dev origin (http://localhost:5173) can never leak into a
  // real client's inbox, and a single config row owns the
  // production URL for every transactional email at once.
  // `opts.approvalBaseUrl` is accepted but ignored -- kept on the
  // signature so existing callers compile until the next pass.
  void opts.approvalBaseUrl;

  const site = await getSiteConfig().catch(() => null);
  const companyName = site?.company_short_name ?? 'PeptideClients';
  const totalFormatted = formatMoney(order.total_cents, order.currency);
  const clientName =
    (typeof snapshot.display_name === 'string' && snapshot.display_name) ||
    (typeof snapshot.first_name === 'string' && snapshot.first_name) ||
    'there';

  let result: SendEmailResult;
  try {
    result = await sendEmail({
      templateKey: 'proposal_sent',
      to: recipient,
      orgId,
      variables: {
        client_name: clientName,
        company_name: companyName,
        order_number: order.number,
        total_formatted: totalFormatted,
        approval_token: token,
      },
    });
  } catch (err) {
    result = { ok: false, error: err instanceof Error ? err.message : String(err) };
  }

  if (result.ok) {
    await safeRecord(
      ctx,
      order.id,
      `${eventPrefix}_sent`,
      `Email sent to ${recipient}.`,
      { to: recipient, message_id: result.message_id ?? null },
    );
    return { status: 'sent', to: recipient, messageId: result.message_id };
  }

  await safeRecord(
    ctx,
    order.id,
    `${eventPrefix}_failed`,
    `Email send failed for ${recipient}: ${result.error ?? 'unknown error'}`,
    { to: recipient, error: result.error ?? null },
  );
  return { status: 'failed', to: recipient, error: result.error ?? 'Email send failed.' };
}

/**
 * SMS counterpart of `sendProposalEmailInternal`. Same shape: always
 * returns an outcome, never throws, records an order event describing
 * what happened so the operator can see it in the activity feed.
 *
 * Recipient resolution: explicit `opts.to` wins; otherwise the
 * `client_snapshot.phone` set when the order was created. The value is
 * normalized through `toE164` -- if the snapshot phone is missing or
 * cannot be coerced into E.164, the send is skipped with a clear
 * reason instead of fired-and-forgotten with a Sendblue 4xx.
 *
 * The `proposal_sent` SMS template is one segment, body:
 *   `{{company_name}}: Your proposal {{order_number}} for
 *    {{total_formatted}} is ready. Review & approve:
 *    {{app_url}}/p/{{approval_token}}`
 *
 * `{{app_url}}` is auto-injected by `core.rpc_emit_sms` from
 * `core.email_settings.default_app_url` (same source as the email
 * template) so the operator's dev origin can never leak into a real
 * client's text message.
 */
async function sendProposalSmsInternal(
  ctx: ActiveContext,
  orgId: string,
  order: Order,
  token: string,
  opts: {
    to?: string;
    eventTypePrefix?: string;
  },
): Promise<ProposalSmsOutcome> {
  const snapshot = (order.client_snapshot ?? {}) as Record<string, unknown>;
  const snapshotPhone =
    typeof snapshot.phone === 'string' && snapshot.phone.trim().length > 0
      ? snapshot.phone.trim()
      : null;
  const requested =
    opts.to && opts.to.trim().length > 0 ? opts.to.trim() : snapshotPhone;

  const eventPrefix = opts.eventTypePrefix ?? 'proposal_sms';
  const recipient = toE164(requested);

  if (!recipient) {
    const reason = requested
      ? `Could not normalize phone "${requested}" to E.164 -- expected +15551234567.`
      : 'No phone number on file for this client.';
    await safeRecord(ctx, order.id, `${eventPrefix}_skipped`, reason, { reason, requested });
    return { status: 'skipped', reason };
  }

  const site = await getSiteConfig().catch(() => null);
  const companyName = site?.company_short_name ?? 'PeptideClients';
  const totalFormatted = formatMoney(order.total_cents, order.currency);

  let result: SendSmsResult;
  try {
    result = await sendSms({
      templateKey: 'proposal_sent',
      to: recipient,
      orgId,
      storeId: order.store_id ?? null,
      variables: {
        company_name: companyName,
        order_number: order.number,
        total_formatted: totalFormatted,
        approval_token: token,
      },
    });
  } catch (err) {
    result = { ok: false, error: err instanceof Error ? err.message : String(err) };
  }

  if (result.ok) {
    await safeRecord(
      ctx,
      order.id,
      `${eventPrefix}_sent`,
      `SMS sent to ${recipient}.`,
      {
        to: recipient,
        message_id: result.message_id ?? null,
        message_kind: result.message_kind ?? null,
      },
    );
    return { status: 'sent', to: recipient, messageId: result.message_id };
  }

  await safeRecord(
    ctx,
    order.id,
    `${eventPrefix}_failed`,
    `SMS send failed for ${recipient}: ${result.error ?? 'unknown error'}`,
    { to: recipient, error: result.error ?? null },
  );
  return { status: 'failed', to: recipient, error: result.error ?? 'SMS send failed.' };
}

async function safeRecord(
  ctx: ActiveContext,
  orderId: string,
  eventType: string,
  summary: string,
  details: Json,
): Promise<void> {
  try {
    await recordEvent(ctx, orderId, eventType, summary, details);
  } catch (err) {
    console.warn('[orders] failed to record event', eventType, err);
  }
}

function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

/**
 * In-office approval for pickup orders — skips the client proposal email/SMS
 * flow and moves directly to `approved`.
 */
export async function operatorApproveOrder(
  ctx: ActiveContext,
  id: string,
): Promise<Order> {
  requireContext(ctx);
  // RPC ships ahead of generated.ts until the next db:types run.
  const { data, error } = await (supabase() as any)
    .schema('orders')
    .rpc('rpc_operator_approve_order', { p_order_id: id });
  if (error) throw error;
  return data as unknown as Order;
}

export async function markInvoiced(
  ctx: ActiveContext,
  id: string,
  dueAt?: string | null,
): Promise<Order> {
  requireContext(ctx);
  // The invoice prefix + sequence are scoped to the order's STORE (not
  // the org), so we look it up first instead of using ctx.storeId in
  // case the operator pivots context between create and invoice. The
  // store-level prefix is honored too.
  const { data: orderRow, error: lookupErr } = await supabase()
    .schema('orders')
    .from('orders')
    .select('store_id')
    .eq('id', id)
    .single();
  if (lookupErr) throw lookupErr;
  const storeId = (orderRow as { store_id: string }).store_id;
  const prefix = await getStorePrefix(storeId, 'invoice');
  const number = await nextNumber(storeId, prefix);
  const { data, error } = await supabase()
    .schema('orders')
    .from('orders')
    .update({
      status: 'invoiced',
      invoiced_at: new Date().toISOString(),
      number,
      due_at: dueAt ?? undefined,
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  await recordEvent(ctx, id, 'invoiced', 'Invoice issued.', { number });
  return data as Order;
}

export async function cancelOrder(
  ctx: ActiveContext,
  id: string,
  reason?: string,
): Promise<Order> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('orders')
    .from('orders')
    .update({
      status: 'cancelled',
      cancelled_at: new Date().toISOString(),
      cancelled_reason: reason ?? undefined,
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  await recordEvent(ctx, id, 'cancelled', 'Order cancelled.', { reason });
  return data as Order;
}

export async function voidOrder(
  ctx: ActiveContext,
  id: string,
  reason?: string,
): Promise<Order> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('orders')
    .from('orders')
    .update({
      status: 'voided',
      voided_at: new Date().toISOString(),
      voided_reason: reason ?? undefined,
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  await recordEvent(ctx, id, 'voided', 'Order voided.', { reason });
  return data as Order;
}

export async function markFulfilled(ctx: ActiveContext, id: string): Promise<Order> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('orders')
    .from('orders')
    .update({ status: 'fulfilled', fulfilled_at: new Date().toISOString() })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  await recordEvent(ctx, id, 'fulfilled', 'Order fulfilled.');
  return data as Order;
}

export async function markPickedUp(
  ctx: ActiveContext,
  id: string,
  pickedUpBy: string | null,
): Promise<Order> {
  requireContext(ctx);
  const now = new Date().toISOString();
  const { data, error } = await supabase()
    .schema('orders')
    .from('orders')
    .update({
      status: 'fulfilled',
      fulfilled_at: now,
      picked_up_at: now,
      picked_up_by: pickedUpBy,
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  await recordEvent(
    ctx,
    id,
    'picked_up',
    pickedUpBy ? `Picked up by ${pickedUpBy}.` : 'Picked up.',
  );
  return data as Order;
}

// ---- payments ------------------------------------------------------------

export async function listOrderPayments(
  ctx: ActiveContext,
  orderId: string,
): Promise<Payment[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('orders')
    .from('payments')
    .select('*')
    .eq('order_id', orderId)
    .order('paid_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  // Generated Row type lags the source_* columns added by the
  // 20260606000200 migration; the runtime row includes them.
  return (data ?? []) as Payment[];
}

export interface RecordPaymentInput {
  orderId: string;
  amountCents: number;
  method: PaymentMethod;
  reference?: string | null;
  notes?: string | null;
  paidAt?: string | null;
}

export async function recordPayment(
  ctx: ActiveContext,
  input: RecordPaymentInput,
): Promise<Payment> {
  const { orgId, storeId } = requireContext(ctx);
  const { data: userData } = await supabase().auth.getUser();
  const row: PaymentInsert = {
    org_id: orgId,
    store_id: storeId,
    order_id: input.orderId,
    amount_cents: input.amountCents,
    method: input.method,
    status: 'succeeded',
    reference: input.reference ?? undefined,
    notes: input.notes ?? undefined,
    recorded_by: userData.user?.id ?? undefined,
    paid_at: input.paidAt ?? new Date().toISOString(),
  };
  const { data, error } = await supabase()
    .schema('orders')
    .from('payments')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  await recordEvent(ctx, input.orderId, 'payment_recorded', `Payment of ${input.amountCents} cents recorded.`, {
    method: input.method,
    reference: input.reference,
  });
  return data as Payment;
}

export async function deletePayment(ctx: ActiveContext, id: string): Promise<void> {
  requireContext(ctx);
  const { error } = await supabase().schema('orders').from('payments').delete().eq('id', id);
  if (error) throw error;
}

// ---- sale alerts (operator order alerts) ---------------------------------

export type SaleAlertType =
  | 'hold_payment'
  | 'hold_billing'
  | 'priority'
  | 'address_issue'
  | 'custom'
  | 'other';

export type SaleAlertSeverity = 'info' | 'warning' | 'critical';

export const SALE_ALERT_TYPE_LABELS: Record<SaleAlertType, string> = {
  hold_payment: 'Hold — payment',
  hold_billing: 'Hold — billing',
  priority: 'Priority',
  address_issue: 'Address issue',
  custom: 'Custom',
  other: 'Other',
};

/**
 * Operator-authored order alert (`orders.sale_alerts`, OfficeHub workflow
 * parity). The table shipped with the OH migration wave
 * (20260625001900) and carries the imported alerts; this service layer is
 * the native read/write path. Codegen lags the table, so the Row type is
 * hand-maintained here (same pattern as `SourceColumns`).
 */
export interface SaleAlert {
  id: string;
  org_id: string;
  store_id: string | null;
  order_id: string | null;
  alert_type: SaleAlertType;
  severity: SaleAlertSeverity;
  condition_type: string | null;
  condition_met_at: string | null;
  hold_until: string | null;
  title: string;
  message: string | null;
  tracking_number: string | null;
  is_active: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  resolved_by_name: string | null;
  resolution_note: string | null;
  source_label: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  created_by_name: string | null;
}

/**
 * Does this alert hard-block packing/shipping of the order's shipments?
 * MUST mirror `fulfillment.fn_shipment_block_reasons` (migration
 * 20260704001600): an active alert blocks when
 *   - `hold_until` is set and still in the future (date-driven hold —
 *     auto-releases when the date arrives, even if still active), or
 *   - `hold_until` is null and it's a hold type or critical severity.
 * Plain info/warning alerts with no hold date stay informational.
 * The RPCs enforce this server-side; the UI uses this to disable buttons.
 */
export function isBlockingSaleAlert(
  alert: Pick<SaleAlert, 'is_active' | 'alert_type' | 'severity' | 'hold_until'>,
): boolean {
  if (!alert.is_active) return false;
  if (alert.hold_until) {
    // hold_until is a DATE ('YYYY-MM-DD'); blocks while today < hold_until
    // (string compare is safe for ISO dates). Matches SQL `> current_date`.
    const today = new Date().toISOString().slice(0, 10);
    return alert.hold_until > today;
  }
  return (
    alert.alert_type === 'hold_payment' ||
    alert.alert_type === 'hold_billing' ||
    alert.severity === 'critical'
  );
}

/** `orders.sale_alerts` is absent from generated.ts (codegen lag). */
function saleAlertsTable() {
  return (supabase().schema('orders') as any).from('sale_alerts');
}

/**
 * Reusable alert snippet (OfficeHub parity) for quick-selecting an order
 * alert. Org-scoped (`store_id` null = org-wide); seeded with the curated
 * library and extendable per org. Codegen lags the table, so the Row type
 * is hand-maintained here.
 */
export interface SaleAlertTemplate {
  id: string;
  org_id: string;
  store_id: string | null;
  alert_type: SaleAlertType;
  severity: SaleAlertSeverity;
  title: string;
  message: string | null;
  position: number;
  created_at: string;
  updated_at: string;
}

/** `orders.sale_alert_templates` is absent from generated.ts (codegen lag). */
function saleAlertTemplatesTable() {
  return (supabase().schema('orders') as any).from('sale_alert_templates');
}

/**
 * The org's active alert templates, ordered for the quick-pick library.
 * Bounded — the seeded library is ~9 rows; a few hundred custom ones is a
 * generous ceiling for a single-select chip row.
 */
export async function listSaleAlertTemplates(
  ctx: ActiveContext,
): Promise<SaleAlertTemplate[]> {
  const { orgId } = requireContext(ctx);
  const { data, error } = await saleAlertTemplatesTable()
    .select('id, org_id, store_id, alert_type, severity, title, message, position, created_at, updated_at')
    .eq('org_id', orgId)
    .is('archived_at', null)
    .order('position', { ascending: true })
    .order('title', { ascending: true })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as SaleAlertTemplate[];
}

/**
 * Resolve the acting operator's id + display name for the
 * created_by/resolved_by attribution columns. Best-effort on the name:
 * a missing core.users row degrades to the auth email, never throws.
 */
async function actingUser(): Promise<{ id: string | null; name: string | null }> {
  const { data: userData } = await supabase().auth.getUser();
  const id = userData.user?.id ?? null;
  if (!id) return { id: null, name: null };
  try {
    const { data } = await supabase()
      .schema('core')
      .from('users')
      .select('display_name, email')
      .eq('id', id)
      .maybeSingle();
    const name =
      (data?.display_name && data.display_name.trim()) ||
      data?.email ||
      userData.user?.email ||
      null;
    return { id, name };
  } catch {
    return { id, name: userData.user?.email ?? null };
  }
}

/**
 * All alerts on one order: active first (newest within each group).
 * Bounded — an order carrying more than 200 alerts is a data problem,
 * not a UI one.
 */
export async function listSaleAlertsForOrder(
  ctx: ActiveContext,
  orderId: string,
): Promise<SaleAlert[]> {
  requireContext(ctx);
  const { data, error } = await saleAlertsTable()
    .select('*')
    .eq('order_id', orderId)
    .order('is_active', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as SaleAlert[];
}

export interface CreateSaleAlertInput {
  orderId: string;
  alertType: SaleAlertType;
  severity: SaleAlertSeverity;
  title: string;
  message?: string | null;
  holdUntil?: string | null;
}

/**
 * Attach an operator alert to an order. org/store are taken from the
 * ORDER row (not the active ctx) so a hub operator alerting a
 * sibling-store order files it under the right tenant scope.
 */
export async function createSaleAlert(
  ctx: ActiveContext,
  input: CreateSaleAlertInput,
): Promise<SaleAlert> {
  requireContext(ctx);
  const { data: orderRow, error: lookupErr } = await supabase()
    .schema('orders')
    .from('orders')
    .select('org_id, store_id')
    .eq('id', input.orderId)
    .single();
  if (lookupErr) throw lookupErr;
  const who = await actingUser();
  const { data, error } = await saleAlertsTable()
    .insert({
      org_id: (orderRow as { org_id: string }).org_id,
      store_id: (orderRow as { store_id: string | null }).store_id,
      order_id: input.orderId,
      alert_type: input.alertType,
      severity: input.severity,
      title: input.title.trim(),
      message: input.message?.trim() || null,
      hold_until: input.holdUntil || null,
      is_active: true,
      created_by: who.id,
      created_by_name: who.name,
    })
    .select('*')
    .single();
  if (error) throw error;
  await safeRecord(
    ctx,
    input.orderId,
    'alert_created',
    `Alert added (${input.severity}): ${input.title.trim()}`,
    { alert_id: (data as SaleAlert).id, alert_type: input.alertType, severity: input.severity },
  );
  return data as SaleAlert;
}

/**
 * Resolve (deactivate) an alert. Keeps the row for history — resolved
 * alerts stay visible in the order's alert log with who/when/why.
 */
export async function resolveSaleAlert(
  ctx: ActiveContext,
  input: { id: string; note?: string | null },
): Promise<SaleAlert> {
  requireContext(ctx);
  const who = await actingUser();
  const { data, error } = await saleAlertsTable()
    .update({
      is_active: false,
      resolved_at: new Date().toISOString(),
      resolved_by: who.id,
      resolved_by_name: who.name,
      resolution_note: input.note?.trim() || null,
    })
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  const alert = data as SaleAlert;
  if (alert.order_id) {
    await safeRecord(
      ctx,
      alert.order_id,
      'alert_resolved',
      `Alert resolved: ${alert.title}`,
      { alert_id: alert.id, resolution_note: alert.resolution_note },
    );
  }
  return alert;
}

/** Flip a resolved alert back on (mis-click recovery). */
export async function reactivateSaleAlert(
  ctx: ActiveContext,
  id: string,
): Promise<SaleAlert> {
  requireContext(ctx);
  const { data, error } = await saleAlertsTable()
    .update({
      is_active: true,
      resolved_at: null,
      resolved_by: null,
      resolved_by_name: null,
      resolution_note: null,
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  const alert = data as SaleAlert;
  if (alert.order_id) {
    await safeRecord(ctx, alert.order_id, 'alert_reactivated', `Alert reactivated: ${alert.title}`, {
      alert_id: alert.id,
    });
  }
  return alert;
}

// ---- public proposal RPCs (used by /p/:token page) ----------------------

export interface ProposalView {
  id: string;
  number: string;
  status: OrderStatus;
  currency: string;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  shipping_cents: number;
  total_cents: number;
  amount_paid_cents: number;
  proposal_message: string | null;
  proposal_sent_at: string | null;
  proposal_expires_at: string | null;
  client_approved_at: string | null;
  client_declined_at: string | null;
  client_snapshot: Record<string, unknown>;
  billing_address_snapshot: Record<string, unknown> | null;
  shipping_address_snapshot: Record<string, unknown> | null;
  company_short_name: string | null;
  contact_email: string | null;
  /**
   * Per-store brand block. Populated by orders.rpc_get_proposal from
   * core.store_settings (contact_email borrows from a same-brand sibling
   * store in the org when unset). Display name falls back to core.stores.name
   * and finally core.site_config. Never surfaces platform contact_email.
   * Frontends should prefer this over the legacy top-level fields above.
   * Missing on older deployments before the 20260525021200 migration ran,
   * hence optional.
   */
  store?: {
    id: string;
    name: string | null;
    display_name: string | null;
    contact_email: string | null;
    reply_to_email?: string | null;
    contact_phone: string | null;
    contact_address: string | null;
    logo_url: string | null;
    website_url: string | null;
  };
  lines: Array<{
    id: string;
    position: number;
    description: string;
    quantity: number;
    unit_price_cents: number;
    discount_cents: number;
    line_total_cents: number;
  }>;
}

export async function publicGetProposal(token: string): Promise<ProposalView> {
  const { data, error } = await supabase()
    .schema('orders')
    .rpc('rpc_get_proposal', { p_token: token });
  if (error) throw error;
  return data as unknown as ProposalView;
}

export interface PublicContractAcceptance {
  proposal_contract_id: string;
  acceptor_name: string;
  consent_electronic_signature: boolean;
  signature_text?: string;
}

export interface PublicApprovalResult {
  ok: boolean;
  order_id?: string;
  notified?: number;
  failed?: number;
}

/**
 * Approve a proposal from the public client-facing page.
 *
 * Routes through the `notify-proposal` Edge Function so that:
 *   - any attached contracts get ESIGN-compliant acceptances recorded,
 *   - the sales rep + org owners/admins get a notification email,
 *   - the response surfaces the count of recipients we reached.
 *
 * Falls back to the bare `orders.rpc_approve_proposal` RPC if the function
 * is unreachable (offline dev, missing Resend config) so the approval
 * still completes for the client. Acceptances are NOT recoverable on the
 * fallback path -- they're only persisted via the function.
 */
export async function publicApproveProposal(
  token: string,
  approvalName: string,
  acceptances: PublicContractAcceptance[] = [],
): Promise<PublicApprovalResult> {
  try {
    const { data, error } = await supabase().functions.invoke<PublicApprovalResult>(
      'notify-proposal',
      {
        body: {
          token,
          action: 'approve',
          name: approvalName,
          acceptances,
          // `app_url` intentionally omitted -- the edge function reads
          // it from core.email_settings so client URLs never come
          // from the (possibly localhost) browser origin.
        },
      },
    );
    if (error) throw error;
    if (data?.ok) return data;
    // Function reachable but reported failure -- surface it.
    throw new Error((data as unknown as { error?: string })?.error ?? 'Approval failed.');
  } catch (fnErr) {
    // Fallback: at least try to transition state via the raw RPC so the
    // client isn't blocked when the edge function or email infra is down.
    if (acceptances.length > 0) {
      // We can't accept contracts without the function path; rethrow so
      // the page can ask the operator to retry.
      throw fnErr;
    }
    const { error } = await supabase()
      .schema('orders')
      .rpc('rpc_approve_proposal', { p_token: token, p_approval_name: approvalName });
    if (error) throw error;
    return { ok: true, notified: 0 };
  }
}

export async function publicDeclineProposal(
  token: string,
  reason?: string | null,
): Promise<PublicApprovalResult> {
  try {
    const { data, error } = await supabase().functions.invoke<PublicApprovalResult>(
      'notify-proposal',
      {
        body: {
          token,
          action: 'decline',
          reason: reason ?? undefined,
          // `app_url` intentionally omitted -- the edge function reads
          // it from core.email_settings so client URLs never come
          // from the (possibly localhost) browser origin.
        },
      },
    );
    if (error) throw error;
    if (data?.ok) return data;
    throw new Error((data as unknown as { error?: string })?.error ?? 'Decline failed.');
  } catch (fnErr) {
    void fnErr;
    const { error } = await supabase()
      .schema('orders')
      .rpc('rpc_decline_proposal', { p_token: token, p_reason: reason ?? undefined });
    if (error) throw error;
    return { ok: true, notified: 0 };
  }
}
